import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';

import { DATA_SOURCES } from 'constants/appDataSources';
import { setAppDataSourceId, setAppDataSourceAppConfig } from 'store/features/appDataSourceSlice';
import useAppDataSource from 'hooks/useAppDataSource';
import callAzureFunction from 'utils-ts/call-azure-function';
import handleError from 'utils-ts/handle-error';
import { useGetEntityName } from 'utils/entities';
import appDataSourceMenuImage from 'assets/images/app_data_source_menu.png';

const DataSourceMenu = () => {
  const dispatch = useDispatch();
  const { appDataSourceIdUserSelection, appDataSourceImplemented, appDataSourceDisabled, useGetAppDataSources, isAppDataSourceRemote } =
    useAppDataSource();
  const appDataSources = useGetAppDataSources();

  const respondentEntityName = useGetEntityName('respondent');
  const bookingEntityName = useGetEntityName('booking');

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [dataSources, setDataSources] = useState(Object.values(DATA_SOURCES));
  useEffect(() => {
    setDataSources(Object.values(appDataSources));
  }, [appDataSources]);

  const getRemoteAppConfig = useCallback(async () => {
    try {
      const response = await callAzureFunction({
        url: `public/app-configurations`,
        method: 'get',
        appDataSourceIdUserSelection
      });
      if (response?.data?.config) {
        dispatch(setAppDataSourceAppConfig(JSON.parse(response.data.config)));
      } else if (response?.data?.configs) {
        dispatch(setAppDataSourceAppConfig(response.data.configs));
      }
    } catch (error) {
      handleError(error);
      console.error('Error fetching remote app configuration:', error);
    }
  }, [appDataSourceIdUserSelection, dispatch]);

  useEffect(() => {
    if (isAppDataSourceRemote) {
      getRemoteAppConfig();
    }
  }, [isAppDataSourceRemote, getRemoteAppConfig]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangeDataSource = (source) => {
    dispatch(setAppDataSourceId(source.id));
    handleClose();
  };

  const appDataSourceDefault = appDataSources.default || DATA_SOURCES.default;

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  return (
    <>
      {dataSources.length > 1 ? (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            id="data-source-button"
            aria-controls={open ? 'data-source-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleOpen}
            endIcon={<ExpandMoreIcon />}
            disabled={appDataSourceDisabled || !appDataSourceImplemented}
          >
            <span>
              {!appDataSourceImplemented
                ? appDataSourceDefault.name
                : dataSources.find((source) => source.id === appDataSourceIdUserSelection)?.name || 'N/A'}
            </span>
          </Button>
          {appDataSourceImplemented && !appDataSourceDisabled && (
            <>
              <Typography variant="subtitle2" sx={{ ml: 2 }}>
                Not seeing the data you expect?{' '}
              </Typography>
              <InfoIcon onClick={handleDialogOpen} sx={{ verticalAlign: 'middle', fontSize: 20, ml: 1, cursor: 'pointer' }} />
            </>
          )}

          <Menu
            id="data-source-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'data-source-button'
            }}
          >
            {dataSources.map((source) => (
              <MenuItem
                key={source.id}
                onClick={() => handleChangeDataSource(source)}
                selected={source.id === appDataSourceIdUserSelection}
              >
                <span style={{ textTransform: 'capitalize' }}>{source.name}</span>
              </MenuItem>
            ))}
          </Menu>

          <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md">
            <DialogTitle>
              <Typography variant="h5">Not seeing the data you expect?</Typography>
            </DialogTitle>
            <DialogContent>
              <Typography>
                Make sure the environment dropdown is set to the correct environment where the {respondentEntityName} data exists. If the{' '}
                {respondentEntityName} and {bookingEntityName} is created through the Affiliate Clinic then you must select the Affiliate
                Clinic option to view their data.
              </Typography>
              <div style={{ textAlign: 'center', margin: '16px 0' }}>
                <img
                  src={appDataSourceMenuImage}
                  alt="Data Source Selection Menu"
                  style={{ maxWidth: '100%', height: 'auto', border: '1px solid #eee', borderRadius: '4px' }}
                />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      ) : null}
    </>
  );
};

export default DataSourceMenu;
